import React from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import ChartCustomTooltip from "../../common-components/chart-custom-tooltip/chart-custom-tooltip";
import { chartColors } from "../../clients-dashboard-style";
import { getUnpaidChartData } from "./helpers";
import { CustomTooltipProps, LegendObject, TicksProps } from "../../clients-dashboard-types";
import { IGetUnpaidInvoicesData } from "../../../../../services/ClientsDashboardServices.dto";

const UnpaidChart = ({ data }: { data: IGetUnpaidInvoicesData[] }) => {
  const newData = getUnpaidChartData(data);
  const productTypes = Object.keys(newData[0]);
  const getInitialOpacities = () => {
    const initialOpacities: { [key: string]: number } = {};
    productTypes.forEach((type) => (initialOpacities[type] = 1));
    return initialOpacities;
  };

  const [activeSeries, setActiveSeries] = React.useState<Array<string>>([]);
  const [opacities, setOpacities] = React.useState<{ [key: string]: number }>(
    getInitialOpacities()
  );

  const handleMouseEnter = (o: LegendObject) => {
    // Se destaca la línea del chart del producto sobre cuya leyenda se hace hover
    // mediante cambios de opacidad
    const { dataKey, inactive } = o;
    if (inactive) return;

    setOpacities((oldOpacities) => {
      const newOpacities: { [key: string]: number } = {};
      Object.keys(oldOpacities).forEach((opacity) =>
        opacity === dataKey ? (newOpacities[opacity] = 1) : (newOpacities[opacity] = 0.3)
      );
      return newOpacities;
    });
  };

  const handleMouseLeave = (o: LegendObject) => {
    // Se recuperan todas las líneas del chart a opacidad 1
    const { inactive } = o;
    if (inactive) return;

    setOpacities((oldOpacities) => {
      const newOpacities: { [key: string]: number } = {};
      Object.keys(oldOpacities).forEach((opacity) => (newOpacities[opacity] = 1));
      return newOpacities;
    });
  };

  const handleLegendClick = (dataKey: string) => {
    // Desactiva y activa las líneas del chart de los productos seleccionados
    if (activeSeries.includes(dataKey)) {
      setActiveSeries(activeSeries.filter((el) => el !== dataKey));
    } else {
      setActiveSeries((prev) => [...prev, dataKey]);
    }
    handleMouseLeave({ inactive: false, dataKey });
  };

  const renderCustomTick = (tickProps: TicksProps) => {
    const { x, y, index } = tickProps;
    // Se busca la primera aparición de un año para mostrarlo una sóla vez
    const isYearChange = index === 0 || newData[index].year !== newData[index - 1].year;

    return (
      <g transform={`translate(${x},${y})`}>
        {isYearChange && (
          <text x={0} y={0} dy={40} textAnchor="middle" fill="#666" fontSize={20}>
            {newData[index].year}
          </text>
        )}
        <text x={0} y={0} dy={16} textAnchor="middle" fill="#666">
          {newData[index].month}
        </text>
      </g>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={800}>
      <LineChart
        width={400}
        height={400}
        data={newData}
        margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
      >
        <XAxis
          dataKey="month"
          padding={{ left: 30, right: 30 }}
          height={70}
          tick={renderCustomTick}
        />
        <YAxis />
        <Tooltip content={(props: CustomTooltipProps) => <ChartCustomTooltip {...props} withoutZero />} />
        <Legend
          height={36}
          iconType="circle"
          onClick={(props) => handleLegendClick(props.dataKey)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
        <CartesianGrid strokeDasharray="3 3" />
        {productTypes.map((type: string, index) => {
          if (type === "month" || type === "year") return null;
          return (
            <Line
              key={type}
              dataKey={type}
              hide={activeSeries.includes(type)}
              type="linear"
              stroke={chartColors[index]}
              strokeOpacity={(`${type}` in opacities && opacities[type]) || 1}
            />
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default UnpaidChart;
